import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import { TenantSummary } from '@bliks-io/contracts';
import { useCreateTenant, useUpdateTenant } from '@/pages/admin/api/mutations/useTenants';
import { FormDialog } from '@/components/dialogs/FormDialog';
import {
  TenantIdentityFields,
  TenantIdentityFormValues,
  dateInputToIso,
  hubspotCompanyLinkIdOf,
  isoToDateInput,
  toHubspotLinkFields
} from './components/TenantIdentityFields';

interface CreateTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The row being edited. Summary shape, not `Tenant`: the HubSpot company id
   * the form seeds from is derived from the tenant's account (LIC-06) and
   * only exists on the wire shape.
   */
  editingTenant?: TenantSummary;
  /**
   * Opened from an account: the customer is already known, so the dialog
   * neither asks for a company nor resolves one — it sends the account id
   * (LIC-06). The company field belongs to the customer, not the workspace.
   */
  accountId?: string;
}

export function CreateTenantDialog({
  open,
  onOpenChange,
  editingTenant,
  accountId
}: CreateTenantDialogProps) {
  const { t } = useTranslation();
  const createTenant = useCreateTenant();
  const updateTenant = useUpdateTenant();

  const isEditMode = !!editingTenant;

  const defaultValues: TenantIdentityFormValues = {
    name: editingTenant?.name || '',
    slug: editingTenant?.slug || '',
    tenantType: editingTenant?.tenantType || 'customer',
    expiresAt: isoToDateInput(editingTenant?.expiresAt),
    description: editingTenant?.description || '',
    hubspotCompanyLinkId: editingTenant ? hubspotCompanyLinkIdOf(editingTenant) : ''
  };

  const onSubmit = async (
    data: TenantIdentityFormValues,
    form: UseFormReturn<TenantIdentityFormValues>
  ) => {
    // FormDialog hands an edit submit only the DIRTY subset, but the HubSpot
    // link is derived from the id and the tenant type together and either half
    // can be the untouched one — switching the type alone has to move an
    // unchanged id to the other contract field. Read the complete form state.
    const identity = form.getValues();
    // Naming the customer directly wins over resolving them from a company id:
    // the account is already the answer that lookup would produce.
    const customer = accountId
      ? { accountId }
      : toHubspotLinkFields(identity, isEditMode ? 'edit' : 'create');

    if (isEditMode && editingTenant) {
      // Forward the dirty subset and convert just the fields that are present.
      // A trimmed-empty note still goes out as '' so the backend clears it
      // ($unset).
      const { hubspotCompanyLinkId, expiresAt, description, ...changed } = data;
      await updateTenant.mutateAsync({
        id: editingTenant._id,
        data: {
          ...changed,
          ...('expiresAt' in data && { expiresAt: dateInputToIso(expiresAt) }),
          ...('description' in data && { description: description?.trim() ?? '' }),
          ...customer
        }
      });
    } else {
      const description = data.description?.trim();
      await createTenant.mutateAsync({
        name: data.name,
        slug: data.slug,
        tenantType: data.tenantType,
        expiresAt: dateInputToIso(data.expiresAt),
        ...customer,
        description: description || undefined
      });
    }
    onOpenChange(false);
  };

  return (
    <FormDialog<TenantIdentityFormValues>
      open={open}
      onOpenChange={onOpenChange}
      title={t(isEditMode ? 'tenantAdmin.editTenant' : 'tenantAdmin.createTenant')}
      description={t(isEditMode ? 'tenantAdmin.editDescription' : 'tenantAdmin.createDescription')}
      icon={Building2}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      isEditing={isEditMode}
      submitLabel={t(isEditMode ? 'tenantAdmin.updateTenant' : 'tenantAdmin.createTenant')}
      cancelLabel={t('tenantAdmin.cancel')}
      size="md"
    >
      {({ form }) => (
        <div className="space-y-4">
          {/* Opened from an account, the customer is already settled — asking
              again would invite a second, contradicting answer. */}
          <TenantIdentityFields form={form} autoSlug={!isEditMode} showHubspotLink={!accountId} />
        </div>
      )}
    </FormDialog>
  );
}
